import { useRef } from 'react';
import { Button, Card, Col, Empty, Grid, Result, Row, Space, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth, usePermissions } from '../../auth/store';
import { TicketDetail } from './TicketDetail';
import { TicketList } from './TicketList';
import {
  CATEGORY_LABEL,
  FINANCE_CATEGORIES,
  useSupportSummary,
  type SupportCategory,
} from './api';

const ALL_CATEGORIES = Object.keys(CATEGORY_LABEL) as SupportCategory[];

/**
 * Support inbox: requests users open from the app, answered here. The open
 * request lives in `?ticket=<id>` so a refresh (or a pasted link) keeps it.
 */
export function SupportPage() {
  const role = useAuth((s) => s.profile?.role);
  // Permission, not role — the route guard already checked the same key, so
  // this only still exists to keep the page safe if it is ever mounted
  // somewhere else.
  const { can } = usePermissions();
  if (!can('support.view')) {
    return (
      <Result
        status="403"
        title="No access"
        subTitle="You do not have the support.view permission."
      />
    );
  }
  return <SupportInbox financeOnly={role === 'finance'} />;
}

function SupportInbox({ financeOnly }: { financeOnly: boolean }) {
  const qc = useQueryClient();
  const screens = Grid.useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('ticket');
  const detailRef = useRef<HTMLDivElement>(null);
  const summary = useSupportSummary({ refetchInterval: 20_000 });

  function select(id: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('ticket', id);
        return next;
      },
      { replace: true },
    );
    // The panes are stacked on narrow screens; bring the request into view.
    if (!screens.lg) detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const s = summary.data;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space size={12} wrap align="center">
          <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
            Support
          </Typography.Title>
          {s && (
            <Space size={4} wrap>
              <Tag color={s.open > 0 ? 'error' : 'default'}>{s.open} open</Tag>
              <Tag color={s.waitingUser > 0 ? 'processing' : 'default'}>{s.waitingUser} waiting for user</Tag>
              <Tag color={s.unread > 0 ? 'magenta' : 'default'}>{s.unread} unread</Tag>
            </Space>
          )}
          {financeOnly && <Tag>Payment and payout requests only</Tag>}
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => void qc.invalidateQueries({ queryKey: ['admin', 'support'] })}>
          Refresh
        </Button>
      </div>

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={9} xl={8}>
          <TicketList
            selectedId={selectedId}
            onSelect={select}
            summary={s}
            categories={financeOnly ? FINANCE_CATEGORIES : ALL_CATEGORIES}
          />
        </Col>
        <Col xs={24} lg={15} xl={16}>
          <div ref={detailRef} style={{ scrollMarginTop: 16 }}>
            {selectedId ? (
              <TicketDetail key={selectedId} id={selectedId} />
            ) : (
              <Card>
                <Empty style={{ padding: '48px 0' }} description="Select a request to read and reply" />
              </Card>
            )}
          </div>
        </Col>
      </Row>
    </Space>
  );
}
